import React from "react";

/**
 * Shared section/field styling for every full-page trailer record screen — Check In,
 * View Details, and Check Out all render the same 5-section layout (Core Shipment Details,
 * Equipment, Driver Information, Yard Information, Seal & Supervisor Note) so the yard team
 * only has to learn one screen pattern. Keep these tokens here, not duplicated per-file, so
 * the three screens can't silently drift apart.
 */

export const labelClass =
  "block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1";

export const baseInput =
  [
    "w-full border border-gray-300 dark:border-acs-dark-border rounded-md px-3 py-2 text-sm",
    "bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text",
    "[appearance:textfield]",
    "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
  ].join(" ");

// Section card — navy header bar + light content panel, the one visual pattern shared by
// Check In / View Details / Check Out.
export const sectionCardClass =
  "mb-fluid-3 rounded-md overflow-hidden border border-gray-300 dark:border-acs-dark-border " +
  "bg-white dark:bg-acs-dark-surface";

export const sectionHeaderBarClass =
  "bg-[#1e2a74] dark:bg-blue-900/40 text-white dark:text-acs-dark-text " +
  "text-fluid-base font-medium px-fluid-2 py-fluid-1.5";

// Dense 6-column field grid — used for every DisplayDetail-based section.
export const sectionFieldGridClass =
  "grid grid-cols-6 gap-x-fluid-2 gap-y-fluid-3 p-fluid-3 bg-[#F9FAFE] dark:bg-acs-dark-surface";

// Plain padded content (checkboxes/textareas) — used by Seal & Supervisor Note.
export const sectionContentClass = "p-fluid-3 bg-[#F9FAFE] dark:bg-acs-dark-surface";

export const gridSelectClass =
  "w-full bg-transparent outline-none appearance-none cursor-pointer pr-fluid-4 dark:text-acs-dark-text [&>option]:text-gray-900";

export const toolbarPrimaryButtonClass =
  "inline-flex items-center gap-2 rounded-md bg-acs-primary-navy px-3 py-1.5 text-sm font-medium " +
  "text-white shadow-sm transition hover:bg-acs-primary-blue focus:outline-none focus:ring-2 " +
  "focus:ring-acs-accent focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

export const toolbarSecondaryButtonClass =
  "inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm " +
  "font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 " +
  "focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";

export function ErrorHint({ message }) {
  if (!message) return null;
  return <p className="text-xs text-red-600 mt-1 font-roboto">{message}</p>;
}

/**
 * Field box matching DisplayDetail.jsx's exact container styling, for control types
 * DisplayDetail doesn't support (select, file upload) — keeps every field on these screens
 * visually identical whether it's plain text or a picker/upload.
 */
export function FormField({ label, required, error, highlighted = false, className = "", children }) {
  return (
    <div className={`flex flex-col min-w-[clamp(8rem,12vw,10rem)] ${className}`}>
      <span className="text-dense-xs text-gray-500 dark:text-acs-dark-subtext">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      <div
        className={`relative flex items-center justify-between w-full
          px-fluid-2 py-fluid-1.5 mt-fluid-0.25
          ${highlighted && !error ? "bg-amber-50 dark:bg-amber-900/10" : "bg-[#FAFAFB] dark:bg-acs-dark-surface"} text-gray-700 dark:text-acs-dark-text
          rounded-md border ${error ? "border-red-500 ring-1 ring-red-200 dark:ring-red-900/40" : highlighted ? "border-amber-400 dark:border-amber-500" : "border-gray-300 dark:border-acs-dark-border"}
          text-fluid-sm font-inter
          focus-within:ring-2 focus-within:ring-blue-400 dark:focus-within:ring-[#223688]
          focus-within:border-blue-400 dark:focus-within:border-[#223688]
          transition-none ease-in-out`}
      >
        {children}
        {/* FormField only ever wraps a <select> — bake in the dropdown chevron so it visibly
            reads as a constrained picker, not free text (appearance-none on the select strips
            the native OS arrow with nothing replacing it otherwise). */}
        <span className="absolute right-fluid-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-acs-dark-subtext text-fluid-xs">
          ▾
        </span>
      </div>
      {error && <p className="text-fluid-xs text-red-600 mt-fluid-0.5">{error}</p>}
    </div>
  );
}
